/* eslint-disable camelcase */
import {
  Box,
  Button,
  Card,
  LocationOffline,
  LocationOnline,
  MessageBlock,
  Paragraph,
  SubscriberStarIcon,
} from '@deckee/deck-hand';
import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { selectUnitsConfig } from '../../../../services/store/modules/app-controls';
import { formatLatLon } from '../../../helpers/coordinate-helpers';
import SheetLayout from '../../../layouts/SheetLayout/SheetLayout';
import SheetModal from '../../../shared/SheetModal/SheetModal';
import Shimmer from '../../../shared/Shimmer/Shimmer';
import BatteryLevelIndicator from '../components/BatteryLevelIndicator';
import useRelativeTime from '../helpers/relative-time';
import useSignal from '../hooks/use-signal';
import useSelectedSignalAsMapCenter from '../hooks/use-signal-as-map-center';
import useSignalFollows from '../hooks/use-signal-follows';

const SignalMarkerSheet: FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    username,
    timestamp,
    longitude,
    latitude,
    online,
    on_the_water,
    battery_level: batteryLevel,
    error,
    subscribed,
  } = useSignal(id);

  const {
    removingFollow,
    removeFollowError,
    followToBeRemoved,
    resetFollowsToBeRemoved,
    handleUnfollowConfirmation,
  } = useSignalFollows();

  const lastKnownRelativeTime = useRelativeTime(timestamp);
  const theme = useTheme();

  const loading = !(username && timestamp);

  const handleDismissPrompt = () => {
    captureEvent({
      type: 'signal: cancel unfollow prompt',
      payload: {},
      sendToAmplitude: true,
    });

    resetFollowsToBeRemoved();
  };

  useEffect(() => {
    captureEvent({
      type: 'signal: view signal marker',
      payload: {
        id,
        username,
      },
      sendToAmplitude: true,
    });
  }, [id, username]);

  const unitsConfig = useSelector(selectUnitsConfig);

  const coordinateSystem = unitsConfig?.coordinate;
  const formattedLatLon =
    latitude && longitude
      ? formatLatLon(coordinateSystem, latitude, longitude)
      : null;

  useSelectedSignalAsMapCenter();

  if (error) {
    return <Redirect to="/maps/signal" />;
  }

  return (
    <SheetLayout showMenuBar={false} heading="Signal">
      {error}
      <Card mx={2} mb={2}>
        <Card.Body p={3}>
          <Shimmer on={loading}>
            <Box
              fontSize={3}
              fontWeight="500"
              mb={3}
              minHeight="20px"
              display="flex"
              alignItems="center"
            >
              <Box mr="2">{username}</Box>{' '}
              {subscribed && <SubscriberStarIcon />}
            </Box>
          </Shimmer>

          <Shimmer on={loading}>
            <Box fontSize={1} color="darkergrey" mb={2} minHeight="18px">
              {on_the_water ? formattedLatLon : 'Not on the water'}
            </Box>
          </Shimmer>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
          >
            <Shimmer on={loading}>
              {online ? (
                <LocationOnline
                  color={theme.colors.primary}
                  width="22px"
                  height="22px"
                />
              ) : (
                <LocationOffline
                  color={theme.colors.darkergrey}
                  width="22px"
                  height="22px"
                />
              )}
            </Shimmer>
            <Shimmer on={loading}>
              <Box ml={1} fontSize={1} color="darkergrey">
                {online ? 'Active ' : 'Last seen '}
                {lastKnownRelativeTime}
              </Box>

              {online && (
                <Box fontSize={1} color="darkergrey">
                  <BatteryLevelIndicator
                    marginLeft={2}
                    batteryLevel={batteryLevel}
                  />
                </Box>
              )}
            </Shimmer>
          </Box>
        </Card.Body>
      </Card>

      <SheetModal isOpen={!!followToBeRemoved}>
        <Box p="3">
          {removeFollowError && (
            <MessageBlock mb="3" type="error">
              Something went wrong. {followToBeRemoved?.followee.displayName}{' '}
              could not be removed, please try again.
            </MessageBlock>
          )}
          <Paragraph>
            If you change your mind, you&apos;ll have to follow{' '}
            <strong>{followToBeRemoved?.followee.displayName}</strong> again.
          </Paragraph>

          <Button.Group pt="3" wide>
            <Shimmer on={removingFollow}>
              <Button onClick={handleUnfollowConfirmation}>
                {removeFollowError ? 'Retry' : 'Unfollow'}
              </Button>
            </Shimmer>
            <Shimmer on={removingFollow}>
              <Button outlined onClick={handleDismissPrompt}>
                Cancel
              </Button>
            </Shimmer>
          </Button.Group>
        </Box>
      </SheetModal>
    </SheetLayout>
  );
};

export default SignalMarkerSheet;
